import apiClient, { scheduleTokenRefresh, clearTokenRefreshTimer } from './apiClient'
import cookieUtils from '../utils/cookieUtils'

/**
 * Authentication API Service
 * Implements endpoints from AwardsPortal API Gateway
 */

const authService = {
  /**
   * Get the campaigns a user belongs to
   * GET /users/campaigns?username={username}
   * 
   * @param {string} username - The username to look up campaigns for
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Array of campaigns [{ campaignid, campaigncode, campaignname, userid }]
   */
  getCampaigns: async (username, options = {}) => {
    const response = await apiClient.get(
      '/users/campaigns',
      {
        params: { username },
        ...options,
      }
    )
    return response.data
  },

  /**
   * Authenticate a user by campaign and user id
   * POST /users/authenticateusername
   * 
   * @param {string} campaignid - The campaign ID the user selected
   * @param {string} userid - The user ID for the selected campaign
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} User data including userid, campaignid, firstnames, surname, etc.
   */
  authenticateUsername: async (campaignid, userid, options = {}) => {
    const response = await apiClient.post(
      '/users/authenticateusername',
      { campaignid, userid },
      options
    )
    return response.data
  },

  /**
   * Set user password (first time login or forgot password)
   * POST /users/{userid}/setpassword
   * 
   * @param {string} userid - The user ID
   * @param {string} password - The new password
   * @param {string} otp - The OTP code for verification
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Success response
   */
  setPassword: async (userid, password, otp, options = {}) => {
    const response = await apiClient.post(
      `/users/${userid}/setpassword`,
      { password, otp },
      options
    )

    const { accessToken, refreshToken } = response.data;

    if (!accessToken) {
      throw new Error('Authentication failed: No token received')
    }

    cookieUtils.set('authToken', accessToken, null)
    if (refreshToken) {
      cookieUtils.set('refreshToken', refreshToken, null)
    }

    // Schedule automatic token refresh
    scheduleTokenRefresh(accessToken)

    return response.data
  },

  /**
   * Verify user password
   * POST /users/{userid}/verifypassword
   * 
   * @param {string} userid - The user ID
   * @param {string} password - The password to verify
   * @param {string} campaignid - The campaign ID the user is logging into
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Access token data if password is valid
   */
  verifyPassword: async (userid, password, campaignid, options = {}) => {
    const response = await apiClient.post(
      `/users/${userid}/verifypassword`,
      { password, campaignid },
      options
    )

    const { accessToken, refreshToken } = response.data;

    if (!accessToken) {
      throw new Error('Authentication failed: No token received')
    }

    cookieUtils.set('authToken', accessToken, null)
    if (refreshToken) {
      cookieUtils.set('refreshToken', refreshToken, null)
    }

    scheduleTokenRefresh(accessToken)

    return response.data
  },

  /**
   * Change user password
   * PUT /users/{userid}/changepassword
   * 
   * @param {string} userid - The user ID
   * @param {string} currentPassword - The current password
   * @param {string} newPassword - The new password
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Success response
   */
  changePassword: async (userid, currentPassword, newPassword, options = {}) => {
    const response = await apiClient.put(
      `/users/${userid}/changepassword`,
      {
        currentpassword: currentPassword,
        newpassword: newPassword,
      },
      options
    )
    return response.data
  },

  /**
   * Handle forgot password flow
   * POST /users/{userid}/forgotpassword
   * 
   * @param {string} userid - The user ID
   * @param {string} password - The new password to set
   * @param {string} otp - The OTP code for verification
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Success response
   */
  forgotPassword: async (userid, password, otp, options = {}) => {
    const response = await apiClient.post(
      `/users/${userid}/forgotpassword`,
      { password, otp },
      options
    )

    return response.data
  },


  /**
   * Request OTP for user
   * POST /users/{userid}/requestotp
   * 
   * @param {string} userid - The user ID
   * @param {number} otpTypeId - OTP type (1=First-time login, 2=Forgot password, 3=Change password)
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Success response
   */
  requestOTP: async (userid, otpTypeId, options = {}) => {
    const response = await apiClient.post(
      `/users/${userid}/requestotp`,
      { otptypeid: otpTypeId },
      options
    )
    return response.data
  },

  /**
   * Verify OTP for user
   * PUT /users/{userid}/verifyotp
   * 
   * @param {string} userid - The user ID
   * @param {number} otpTypeId - OTP type (1=First-time login, 2=Forgot password, 3=Change password)
   * @param {string} otp - The OTP code to verify
   * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
   * @returns {Promise} Success response
   */
  verifyOTP: async (userid, otpTypeId, otp, options = {}) => {
    const response = await apiClient.put(
      `/users/${userid}/verifyotp`,
      {
        otptypeid: otpTypeId,
        otp,
      },
      options
    )
    return response.data
  },

  /**
   * Refresh access token using refresh token
   * POST /token/refresh
   * 
   * @returns {Promise} New access token
   */
  refreshAccessToken: async () => {
    const refreshToken = cookieUtils.get('refreshToken')

    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await apiClient.post(
      '/token/refresh',
      { token: refreshToken },
      { showSnackbar: false } // Don't show snackbar for automatic refresh
    )

    const { accessToken } = response.data;

    if (!accessToken) {
      throw new Error('Failed to refresh token')
    }

    // Update stored access token (refresh token remains the same)
    cookieUtils.set('authToken', accessToken, null)

    return { accessToken }
  },

  /**
   * Check authentication status
   * Validates stored token and user data from cookies
   * 
   * @returns {Promise} User data if authenticated
   */
  checkAuth: async () => {
    const token = cookieUtils.get('authToken')
    const userData = cookieUtils.get('authUser')

    if (!token || !userData) {
      throw new Error('No valid session')
    }

    // In a real implementation, you might want to validate the token with the backend
    const user = JSON.parse(userData)

    // Schedule automatic token refresh on app initialization
    scheduleTokenRefresh(token)

    return { user, token }
  },

  /**
   * Logout user
   * Clears stored authentication data from cookies
   */
  logout: () => {
    clearTokenRefreshTimer()
    cookieUtils.delete('authToken')
    cookieUtils.delete('refreshToken')
    cookieUtils.delete('authUser')
  },
}

export default authService

// OTP Type Constants for reference
export const OTP_TYPES = {
  FIRST_TIME_LOGIN: 1,
  FORGOT_PASSWORD: 2,
  CHANGE_PASSWORD: 3,
}
